import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { makeHitori, hitoriConflicts, hitoriWin } from "@/lib/hitori";

export function HitoriGame({ n, onWin, resetKey }) {
  const { t } = useTranslation();
  const [puzzle, setPuzzle] = useState(null);
  const [shaded, setShaded] = useState([]);

  const start = useCallback(() => {
    const p = makeHitori(n);
    setPuzzle(p);
    setShaded(Array.from({ length: n }, () => Array(n).fill(false)));
  }, [n]);

  useEffect(() => { start(); }, [start, resetKey]);

  const conflicts = useMemo(() => (puzzle ? hitoriConflicts(shaded, puzzle.val, n) : new Set()), [shaded, puzzle, n]);
  const solved = useMemo(() => (puzzle && shaded.length ? hitoriWin(shaded, puzzle.val, n) : false), [shaded, puzzle, n]);
  useEffect(() => { if (solved) onWin?.(); }, [solved, onWin]);

  const toggle = (r, c) => {
    if (solved) return;
    setShaded((s) => { const ns = s.map((x) => x.slice()); ns[r][c] = !ns[r][c]; return ns; });
  };

  if (!puzzle || !shaded.length) return null;

  return (
    <div className="flex flex-col items-center" data-testid="hitori-game">
      <p className="text-xs text-foreground/45 text-center max-w-md mb-6 leading-relaxed">{t("games.hitoriRules")}</p>
      <div className="grid gap-1 mx-auto" style={{ gridTemplateColumns: `repeat(${n}, minmax(0,1fr))`, width: "min(86vw, 400px)" }} data-testid="hitori-grid">
        {puzzle.val.map((row, r) =>
          row.map((v, c) => {
            const sh = shaded[r][c];
            const bad = conflicts.has(`${r},${c}`);
            return (
              <button
                key={`${r}-${c}`}
                data-testid={`hitori-cell-${r}-${c}`}
                onClick={() => toggle(r, c)}
                className={`aspect-square rounded-md flex items-center justify-center font-display text-base sm:text-lg transition-colors border
                  ${sh ? "bg-[hsl(24_20%_10%)] text-foreground/25 border-white/5" : bad ? "bg-red-500/20 text-red-200 border-red-500/40" : "bg-white/[0.05] text-foreground/85 border-white/10"}`}
              >
                {v}
              </button>
            );
          })
        )}
      </div>
      <button onClick={start} data-testid="hitori-new-btn" className="mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/45 hover:text-primary transition-colors">
        <RefreshCw size={13} /> {t("games.newGrid")}
      </button>
    </div>
  );
}
